import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  SkeletonLine,
  useBreakpoint,
} from '~/techstyle-shared/react-components';

import {
  buildFallbackImageFromCDN,
  getImageUrlFromImageSet,
  getProductImageAspectRatioString,
  getProductImageSizeHeight,
  getProductImageSizeWidth,
} from '../../utils/images';

const ProductCardImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: ${(props) => props.$aspectRatio};
`;

const StyledSkeletonLine = styled(SkeletonLine)`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  transition: opacity 0.5s;
  opacity: ${($isLoading) => ($isLoading ? 1 : 0)};
`;

const StyledImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.5s;
  opacity: ${({ $isLoading }) => ($isLoading ? 0 : 1)};
`;

const ProductCardImageFromImageSet = ({
  images = [],
  swatchLabel,
  showPlusSizeImage = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isMobile } = useBreakpoint();
  const [isLoading, setIsLoading] = useState(true);

  const primaryImageUrl = getImageUrlFromImageSet({
    imageSet: images,
    showPlusSizeImage,
    requestedAngle: '1',
    requestedWidth: getProductImageSizeWidth(isMobile),
    requestedHeight: getProductImageSizeHeight(isMobile),
  });
  const hoverImageUrl = getImageUrlFromImageSet({
    imageSet: images,
    showPlusSizeImage,
    requestedAngle: '2',
    requestedWidth: getProductImageSizeWidth(isMobile),
    requestedHeight: getProductImageSizeHeight(isMobile),
  });

  const handleImageLoaded = (e) => {
    setIsLoading(false);
  };

  const handleImageError = (e) => {
    e.target.onerror = null; // prevents looping
    e.target.src = buildFallbackImageFromCDN({
      width: getProductImageSizeWidth(isMobile),
      height: getProductImageSizeHeight(isMobile),
    });
  };

  // Preload the hover image
  useEffect(() => {
    const image = new Image();
    image.src = hoverImageUrl;
  }, [hoverImageUrl]);

  return (
    <ProductCardImageContainer
      $aspectRatio={getProductImageAspectRatioString(isMobile)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StyledSkeletonLine
        $isLoading={isLoading}
        $height={getProductImageSizeHeight(isMobile)}
        $width={getProductImageSizeWidth(isMobile)}
      />
      <StyledImage
        $isLoading={isLoading}
        src={isHovered ? hoverImageUrl : primaryImageUrl}
        alt={swatchLabel}
        title={swatchLabel}
        onError={handleImageError}
        onLoad={handleImageLoaded}
        data-autotag={isHovered ? 'product-image-on-hover' : 'product-image'}
      />
    </ProductCardImageContainer>
  );
};

ProductCardImageFromImageSet.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      angle: PropTypes.string.isRequired,
      sizes: PropTypes.arrayOf(
        PropTypes.shape({
          width: PropTypes.number.isRequired,
          height: PropTypes.number.isRequired,
          url: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ),
  showPlusSizeImage: PropTypes.bool,
  swatchLabel: PropTypes.string,
};

export default ProductCardImageFromImageSet;
